import React, { useState, useEffect } from "react";
import { Button, Loader } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { ConfirmModal } from "../../../Shared";
import { RiskMatrixHeatMap } from "../RiskMatrixHeatMap/RiskMatrixHeatMap";
import { RiskMatrixCoverage } from "../RiskMatrixCoverage/RiskMatrixCoverage";
import { RISK_MATRIX_EXAMPLES, loadRiskMatrixExamples } from "../riskMatrixExamples";
import "./RiskMatrixWorkspace.scss";

const STEPS = [
  { key: "guide", labelKey: "risk_matrix_step_guide" },
  { key: "load", labelKey: "risk_matrix_step_load" },
  { key: "matrix", labelKey: "risk_matrix_step_matrix" },
  { key: "heat", labelKey: "risk_matrix_step_heat", requiresHeatMap: true },
  { key: "summary", labelKey: "risk_matrix_step_summary" },
];

// Espejo de la vista "ws" (workspace) del prototipo — un wizard de 5 pasos
// por metodología, distinto del Dashboard (resumen de todas). Se entra vía
// "Abrir análisis →" desde el Dashboard de cada metodología (ver
// RiskMatrixWorkspace.scss y ListEnvironmentalRiskMatrixForms.js).
export function RiskMatrixWorkspace({
  methodologyKey,
  tag,
  title,
  onBack,
  Guide,
  Form,
  List,
  fetchBySite,
  hasHeatMap,
  siteSelected,
  reload,
  onReload,
  examplesKey,
  createFn,
  deleteFn,
  buildCoverage,
}) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [step, setStep] = useState("guide");
  const [items, setItems] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    setStep("guide");
  }, [methodologyKey]);

  useEffect(() => {
    (async () => {
      if (!siteSelected) {
        setItems([]);
        return;
      }
      try {
        const response = await fetchBySite(accessToken, siteSelected);
        setItems(response.code === 200 ? response.riskMatrices || [] : []);
      } catch (error) {
        console.error(error);
        setItems([]);
      }
    })();
  }, [siteSelected, reload, accessToken, fetchBySite]);

  const handleFormSaved = () => {
    onReload();
    setFormKey((k) => k + 1);
  };

  const handleLoadExamples = async () => {
    setLoadingExamples(true);
    try {
      await loadRiskMatrixExamples({
        accessToken,
        siteSelected,
        examples: RISK_MATRIX_EXAMPLES[examplesKey],
        createFn,
      });
      toast.success(t("risk_matrix_examples_loaded"));
      onReload();
    } catch (error) {
      toast.error(error?.msg || error?.message || t("server_connection_error"));
    } finally {
      setLoadingExamples(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      for (const item of items || []) {
        await deleteFn(accessToken, item._id);
      }
      toast.success(t("risk_matrix_cleared"));
      onReload();
    } catch (error) {
      toast.error(error?.msg || error?.message || t("server_connection_error"));
    } finally {
      setClearing(false);
      setConfirmClear(false);
    }
  };

  const visibleSteps = STEPS.filter((s) => !s.requiresHeatMap || hasHeatMap);
  const coverageRows = items && buildCoverage ? buildCoverage(items, t) : [];

  return (
    <div className="risk-matrix-workspace">
      <div className="risk-matrix-workspace__topbar">
        <Button className="risk-matrix-workspace__btn risk-matrix-workspace__btn--outline" size="small" onClick={onBack}>
          ← {t("risk_matrix_menu_dashboard")}
        </Button>
        <span className="risk-matrix-workspace__tag">{tag}</span>
        <span className="risk-matrix-workspace__name">{title}</span>
        <div className="risk-matrix-workspace__spacer" />
        <Button
          className="risk-matrix-workspace__btn risk-matrix-workspace__btn--outline"
          size="small"
          loading={loadingExamples}
          disabled={loadingExamples || !items}
          onClick={handleLoadExamples}
        >
          {t("risk_matrix_load_examples_workspace")}
        </Button>
        <Button
          className="risk-matrix-workspace__btn risk-matrix-workspace__btn--danger"
          size="small"
          disabled={!items || items.length === 0}
          onClick={() => setConfirmClear(true)}
        >
          {t("risk_matrix_clear")}
        </Button>
      </div>

      <div className="risk-matrix-workspace__steps">
        {visibleSteps.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`risk-matrix-workspace__step${step === s.key ? " active" : ""}`}
            onClick={() => setStep(s.key)}
          >
            {t(s.labelKey)}
          </button>
        ))}
      </div>

      <div className="risk-matrix-workspace__panel">
        {step === "guide" && (
          <>
            <Guide />
            <div className="risk-matrix-workspace__cta">
              <Button className="risk-matrix-workspace__btn risk-matrix-workspace__btn--solid" onClick={() => setStep("load")}>
                {t("risk_matrix_start_loading")} →
              </Button>
            </div>
          </>
        )}
        {step === "load" && (
          <Form key={formKey} siteSelected={siteSelected} onClose={() => {}} onReload={handleFormSaved} />
        )}
        {step === "matrix" && (
          <List siteSelected={siteSelected} reload={reload} onReload={onReload} embedded />
        )}
        {step === "heat" && hasHeatMap && (
          <RiskMatrixHeatMap siteSelected={siteSelected} reload={reload} fetchBySite={fetchBySite} />
        )}
        {step === "summary" && (!items ? <Loader active inline="centered" /> : <RiskMatrixCoverage rows={coverageRows} />)}
      </div>

      <ConfirmModal
        open={confirmClear}
        title={t("risk_matrix_clear_confirm_title")}
        content={tag}
        loading={clearing}
        confirmText={t("risk_matrix_clear")}
        cancelText={t("cancel")}
        danger
        onConfirm={handleClearAll}
        onCancel={() => setConfirmClear(false)}
      />
    </div>
  );
}
